import React, { Suspense } from "react";
import { createBrowserRouter, Outlet, redirect, RouterProvider, useLocation, type LoaderFunctionArgs } from "react-router-dom";
import "./App.css";

// Actions must be imported eagerly (they run before component renders)
import { action as registerAction } from "./features/auth/pages/register-page";
import { action as loginAction } from "./features/auth/pages/login-page";
import { action as logoutAction } from "./features/auth/logout";
import { AuthUtils } from './utils/helper_functions';
import { gamePageLoader, activeGameRedirectLoader, gameLobbyLoader } from "./utils/loaders";
import { buildLoginPath, getCurrentPathFromRequest, getRedirectToFromRequest } from "./utils/auth-redirect";

// ErrorPage should be eagerly loaded (needed for error boundaries)
import ErrorPage from "./features/home/pages/error-page";

// Analytics
import AnalyticsProvider from "./components/AnalyticsProvider";
import ServiceWorkerUpdateToast from "./components/service-worker-update-toast";
import ActiveGameRecoveryBanner from "./components/active-game-recovery-banner";
import SessionEndNotice from "./components/session-end-notice";

// Global Layout Components
import GlobalHeader from "./UI/common/Layout/global-header";

// Loading fallback component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'linear-gradient(180deg, #000000 15.29%, rgba(0, 0, 0, 0) 100%), conic-gradient(from 34.81deg at 101.94% 101.46%, #475F02 -28.12deg, #4C0700 43.42deg, #533800 144.36deg, #0C2B4F 231.96deg, #475F02 331.88deg, #4C0700 403.42deg)'
  }}>
    <div style={{ textAlign: 'center', color: 'white' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'bounce 1s ease-in-out infinite' }}>🐸</div>
      <div style={{ fontFamily: 'FreckleFace, sans-serif', fontSize: '18px' }}>Loading...</div>
    </div>
  </div>
);

// Lazy load all page components for code splitting
const HomePage = React.lazy(() => import("./features/home/pages/home-page"));
const RegisterPage = React.lazy(() => import("./features/auth/pages/register-page"));
const LoginPage = React.lazy(() => import("./features/auth/pages/login-page"));
const NotFoundPage = React.lazy(() => import("./routes/not-found-page"));

// Flip Card Game pages
const FlipCardGameLayout = React.lazy(() => import("./features/games/flipCardGame/components/Layouts/web-socket-layout"));
const JoinPage = React.lazy(() => import("./features/games/flipCardGame/pages/join-page"));
const LoadPage = React.lazy(() => import("./features/games/flipCardGame/pages/load-page"));
const GamePage = React.lazy(() => import("./features/games/flipCardGame/pages/game-page"));
const GameCompletionPage = React.lazy(() => import('./features/games/flipCardGame/pages/game-completion-page'));
const TestFlipCardGamePage = React.lazy(() => import("./features/games/flipCardGame/pages/test-game-page"));


// Quiz Marathon Game pages
const QuizMarathonLayout = React.lazy(() => import("./features/games/quizMarathon/components/quiz-marathon-layout"));
const QuizMarathonGamePage = React.lazy(() => import("./features/games/quizMarathon/pages/game-page"));
const TestQuizMarathonPage = React.lazy(() => import("./features/games/quizMarathon/pages/test-game-page"));


// Double Down Game pages
const DoubleDownLayout = React.lazy(() => import("./features/games/doubleDown/components/double-down-layout"));
const TestDoubleDownPage = React.lazy(() => import("./features/games/doubleDown/pages/test-game-page"));
const DoubleDownGamePage = React.lazy(() => import("./features/games/doubleDown/pages/game-page"));


// Hop pages
const CreateHopPage = React.lazy(() => import("./features/hop/pages/create-hop-page"));
const AllHopsPage = React.lazy(() => import("./features/hop/pages/all-hops-page"));
const StartHopPage = React.lazy(() => import("./features/hop/pages/start-hop-page"));
const JoinHopPage = React.lazy(() => import("./features/hop/pages/join-hop-page"));
const JoinLinkPage = React.lazy(() => import("./features/hop/pages/join-link-page"));
const LobbyPage = React.lazy(() => import("./features/hop/pages/lobby-page"));
const DashboardPage = React.lazy(() => import("./features/hop/pages/dashboard-page"));

// Game pages
const CreateGamePage = React.lazy(() => import("./features/game/pages/create-game-page"));
const GameLobbyPage = React.lazy(() => import("./features/game/pages/game-lobby-page"));

// Profile pages
const ProfilePage = React.lazy(() => import("./features/profile/pages/profile-page"));
const SessionResultsPage = React.lazy(() => import("./features/profile/pages/session-results-page"));

// About page
const AboutPage = React.lazy(() => import("./features/about/pages/about-page"));

// Game page routes that should NOT have top padding (no header)
const GAME_ROUTES = [
  '/game-lobby/',
  '/flip-card-game/game/',
  '/flip-card-game/hop/',
  '/flip-card-game/completion',
  '/quiz-marathon/game/',
  '/quiz-marathon/hop/',
  '/double-down/game/',
  '/double-down/hop/',
  '/test-bank-game',
  '/test-marathon-quiz',
  '/test-flip-card-game-ui',
];

export const isFullScreenGameRoute = (pathname: string) => (
  GAME_ROUTES.some(route => pathname.startsWith(route))
);

// Wrapper that hides header and removes padding on game pages
const ConditionalHeader: React.FC = () => {
  const location = useLocation();
  const isGamePage = isFullScreenGameRoute(location.pathname);
  if (isGamePage) return null;
  return <GlobalHeader />;
};

const ContentWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isGamePage = isFullScreenGameRoute(location.pathname);
  return (
    <div style={isGamePage ? undefined : { paddingTop: '80px' }}>
      {children}
    </div>
  );
};

const requireAuthLoader = async ({ request }: LoaderFunctionArgs) => {
  const token = AuthUtils.getAuthToken();
  if (!token || !AuthUtils.isTokenValid(token)) {
    return redirect(buildLoginPath(getCurrentPathFromRequest(request)));
  }
  return null;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <AnalyticsProvider>
        <ActiveGameRecoveryBanner />
        <SessionEndNotice />
        <ConditionalHeader />
        <Suspense fallback={<PageLoader />}>
          <ContentWrapper>
            {/* Add layout or wrapper content */}
            <Outlet />
          </ContentWrapper>
        </Suspense>
      </AnalyticsProvider>
    ),
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <HomePage />, loader: activeGameRedirectLoader },
      {
        path: "register", loader: async ({ request }) => {
          const token = AuthUtils.getAuthToken();
          const url = new URL(request.url);
          const isCompletingRegistration = url.searchParams.get("registered") === "true";
          if (token && AuthUtils.isTokenValid(token) && !isCompletingRegistration) {
            return redirect(getRedirectToFromRequest(request));
          }
          return null;
        }, action: registerAction, element: <RegisterPage />
      },
      {
        path: "login", loader: async ({ request }) => {
          const token = AuthUtils.getAuthToken();
          if (token && AuthUtils.isTokenValid(token)) {
            return redirect(getRedirectToFromRequest(request));
          }
          return null;
        }, action: loginAction, element: <LoginPage />
      },
      { path: "logout", loader: logoutAction },
      {
        path: "create-hop",
        element: <CreateHopPage />,
        loader: requireAuthLoader
      },
      {
        path: "hops",
        element: <AllHopsPage />
      },
      {
        path: "start-hop",
        element: <StartHopPage />,
        loader: requireAuthLoader
      },
      {
        path: "join-hop",
        element: <JoinHopPage />
      },
      {
        path: "hop/join/:code",
        element: <JoinLinkPage />
      },
      {
        path: "hop/session/:sessionId/lobby",
        element: <LobbyPage />,
        loader: activeGameRedirectLoader
      },
      {
        path: "hop/session/:sessionId/dashboard",
        element: <DashboardPage />,
        loader: activeGameRedirectLoader
      },
      {
        path: "profile",
        element: <ProfilePage />,
        loader: requireAuthLoader
      },
      {
        path: "profile/session/:sessionId",
        element: <SessionResultsPage />,
        loader: requireAuthLoader
      },
      {
        path: "profile/game-session/:gameSessionId",
        element: <SessionResultsPage />,
        loader: requireAuthLoader
      },
      {
        path: "about",
        element: <AboutPage />
      },
      {
        path: "join-zooma",
        element: <HomePage />,
        loader: activeGameRedirectLoader
      },
      {
        path: "join-bandido",
        element: <HomePage />,
        loader: activeGameRedirectLoader
      },
      {
        path: "join-rolla",
        element: <HomePage />,
        loader: activeGameRedirectLoader
      },
      {
        path: "game-lobby/:gameId",
        element: <GameLobbyPage />,
        loader: gameLobbyLoader
      },
      {
        path: "create-game",
        element: <CreateGamePage />,
        loader: requireAuthLoader
      },
      {
        path: "test-flip-card-game-ui",
        element: <TestFlipCardGamePage />
      },

      {
        path: "test-marathon-quiz",
        element: <TestQuizMarathonPage />
      },

      {
        path: "test-bank-game",
        element: <TestDoubleDownPage />
      },

      {
        path: "flip-card-game",
        element: <FlipCardGameLayout />,
        children: [
          { index: true, element: <JoinPage /> },
          {
            path: "load",
            element: <LoadPage />,
            loader: requireAuthLoader
          },
          { path: "game/:gameId", element: <GamePage />, loader: gamePageLoader },
          { path: "hop/:gameRoomId", element: <GamePage />, loader: gamePageLoader },
        ],
      },
      {
        path: "quiz-marathon",
        element: <QuizMarathonLayout />,
        children: [
          { path: "game/:gameId", element: <QuizMarathonGamePage />, loader: gamePageLoader },
          { path: "hop/:gameRoomId", element: <QuizMarathonGamePage />, loader: gamePageLoader },
        ],
      },
      {
        path: "double-down",
        element: <DoubleDownLayout />,
        children: [
          { path: "game/:gameId", element: <DoubleDownGamePage />, loader: gamePageLoader },
          { path: "hop/:gameRoomId", element: <DoubleDownGamePage />, loader: gamePageLoader },
        ],
      },
      {
        path: "not-found",
        element: <NotFoundPage /> // Create this component
      },
      {
        path: "flip-card-game/completion",
        element: <GameCompletionPage />
      },
      {
        path: "*",
        element: <NotFoundPage />
      }
    ],
  },
]);

function App() {
  return (
    <div className="App">
      <RouterProvider router={router} />
      <ServiceWorkerUpdateToast />
    </div>
  );
}

export default App;
